/**
 * Sprite-Sheets: primär public/sprites8/ (8 Richtungen),
 * Fallback public/sprites/ (4 Richtungen), sonst Kreise.
 */
export type SpriteMeta = {
  cell: number;
  cols: number;
  rows: string[];
  frames: number;
  sprites: Record<string, string>;
};

const FRAME_MS = 160;

let meta: SpriteMeta | null = null;
/** '8' | '4' | null */
let metaSource: '8' | '4' | null = null;
const images = new Map<string, HTMLImageElement>();
const failed = new Set<string>();
let loadPromise: Promise<void> | null = null;

export function getSpriteMeta(): SpriteMeta | null {
  return meta;
}

export function spriteLoadStatus(): {
  ready: boolean;
  loaded: number;
  expected: number;
  failed: string[];
  keys: string[];
  source: '8' | '4' | null;
} {
  const keys = meta ? Object.keys(meta.sprites) : [];
  let loaded = 0;
  for (const k of keys) {
    const img = images.get(k);
    if (img && img.complete && img.naturalWidth > 0 && !failed.has(k)) loaded++;
  }
  return {
    ready: !!meta && loaded === keys.length && failed.size === 0,
    loaded,
    expected: keys.length,
    failed: [...failed],
    keys,
    source: metaSource,
  };
}

export function isSpriteReady(key: string): boolean {
  if (failed.has(key)) return false;
  const img = images.get(key);
  return !!(img && img.complete && img.naturalWidth > 0);
}

async function fetchMeta(url: string): Promise<SpriteMeta | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as SpriteMeta;
  } catch {
    return null;
  }
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function loadSprites(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    const meta8 = await fetchMeta('/sprites8/sprites.json');
    const meta4 = await fetchMeta('/sprites/sprites.json');
    if (meta8 && meta8.rows?.length) {
      meta = meta8;
      metaSource = '8';
    } else if (meta4 && meta4.rows?.length) {
      meta = meta4;
      metaSource = '4';
    } else {
      meta = null;
      metaSource = null;
      console.error('Kein sprites.json geladen');
      return;
    }

    const primaryBase = metaSource === '8' ? '/sprites8/' : '/sprites/';
    const fallbackBase = metaSource === '8' ? '/sprites/' : null;
    const entries = Object.entries(meta.sprites);

    await Promise.all(
      entries.map(async ([key, file]) => {
        let img = await loadImage(primaryBase + file);
        if (!img && fallbackBase) {
          img = await loadImage(fallbackBase + file);
        }
        if (img) {
          images.set(key, img);
        } else {
          failed.add(key);
          console.warn('Sprite fehlt:', key, file);
        }
      }),
    );
  })();
  return loadPromise;
}

/**
 * Richtung aus Vektor.
 * 8 Richtungen (sprites8): 45°-Sektoren, Reihenfolge wie in sprites.json.
 * 4 Richtungen (Fallback): down/left/right/up.
 */
export function dirRowFromVec(dx: number, dy: number, lastRow: number): number {
  const n = meta?.rows.length ?? 4;
  if (Math.abs(dx) < 1e-3 && Math.abs(dy) < 1e-3) return lastRow;
  if (n >= 8) {
    // atan2: x rechts, y unten positiv (Canvas)
    let ang = Math.atan2(dx, dy); // 0 = down, +pi/2 = right
    if (ang < 0) ang += Math.PI * 2;
    const sector = (Math.round(ang / (Math.PI / 4)) % 8) as number;
    // 0 down, 1 down_right, 2 right, 3 up_right, 4 up, 5 up_left, 6 left, 7 down_left
    // sprites.json: down, down_left, left, up_left, up, up_right, right, down_right
    const map = [0, 7, 6, 5, 4, 3, 2, 1];
    return map[sector] ?? 0;
  }
  // 4er: down left right up
  if (Math.abs(dy) >= Math.abs(dx)) return dy > 0 ? 0 : 3;
  return dx > 0 ? 2 : 1;
}

export type DirRow = number;

export type AnimState = {
  frame: number;
  accMs: number;
  row: DirRow;
};

export function tickAnim(
  anim: AnimState,
  moving: boolean,
  dx: number,
  dy: number,
  dt: number,
  frames: number,
): void {
  anim.row = dirRowFromVec(dx, dy, anim.row);
  if (!moving) {
    anim.frame = 0;
    anim.accMs = 0;
    return;
  }
  anim.accMs += dt * 1000;
  while (anim.accMs >= FRAME_MS) {
    anim.accMs -= FRAME_MS;
    anim.frame = (anim.frame + 1) % Math.max(1, frames);
  }
}

export function drawSprite(
  ctx: CanvasRenderingContext2D,
  key: string,
  cx: number,
  cy: number,
  size: number,
  row: number,
  frame: number,
): boolean {
  if (!meta || failed.has(key)) return false;
  const img = images.get(key);
  if (!img || !img.complete || img.naturalWidth <= 0) return false;
  const cell = meta.cell;
  const frames = meta.frames;
  const rowCount = meta.rows.length;
  const r = ((row % rowCount) + rowCount) % rowCount;
  const f = ((frame % frames) + frames) % frames;
  const sx = f * cell;
  const sy = r * cell;
  const prev = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, sx, sy, cell, cell, cx - size / 2, cy - size / 2, size, size);
  ctx.imageSmoothingEnabled = prev;
  return true;
}

export function spriteKeyForPerson(kind: string, tier: string): string {
  if (kind === 'vip') return 'vip';
  if (kind === 'nobody') return 'nobody';
  if (tier === 'gross') return 'grosskunde';
  if (tier === 'hart') return 'kunde_hart';
  return 'kunde_normal';
}

export function spriteKeyForPlayer(isHuman: boolean, rivalIndex: number): string {
  if (isHuman) return 'player';
  const n = ((rivalIndex % 4) + 4) % 4;
  return `rival_${n + 1}`;
}
